import { Component, Prop, h, Element, State, Host, EventEmitter, Event } from '@stencil/core';
import { MarketWebContextService } from '../../../globals/MarketWebContextService';
import { FetchHelper } from '../../../globals/FetchHelper';
import { Root, PostalCodeProps, CityProps, StreetAddressProps, StreetNumberProps } from './types';
import debounce from 'debounce';
import { translations } from './translations';
import { alert_exclamation_circle } from 'pn-design-assets/pn-assets/icons';

@Component({
  tag: 'pn-address-autofill',
  styleUrl: 'pn-address-autofill.scss',
})
export class PnAddressAutofill {
  @Element() hostElement: Element;
  pnInputPostalCode: HTMLElement = null;
  pnInputCity: HTMLElement = null;
  pnInputStreetAddress: HTMLElement = null;
  pnInputStreetNumber: HTMLElement = null;
  postalCodeInputField: HTMLInputElement = null;
  streetAddressInputField: HTMLInputElement = null;
  streetNumberInputField: HTMLInputElement = null;
  pnInputHiddenValue: HTMLElement = null;
  endpointPath = '/api/location/get-by-postalcode';
  private _postalCodeProps: PostalCodeProps = null;
  private _cityProps: CityProps = null;
  private _streetAddressProps: StreetAddressProps = null;
  private _streetNumberProps: StreetNumberProps = null;

  /** Specifies which market we want to show (se,dk,fi,no,com) */
  @Prop({ mutable: true, reflect: true }) market: string = null; //se
  /** Specifies which environment we're fetching data from. (production, preproduction, integration, localhost) */
  @Prop({ mutable: true, reflect: true }) environment: string = null; //sv
  @Prop() endpoint: string = null;
  @Prop() cache: boolean = true;
  @Prop() countryCode: string = null;
  @Prop() language: string = null;

  @Prop() postalCodeProps: string | PostalCodeProps = null;
  @Prop() cityProps: string | CityProps = null;
  @Prop() streetAddressProps: string | StreetAddressProps = null;
  @Prop() streetNumberProps: string | StreetNumberProps = null;
  @Prop() addressNotFoundError: string = null;

  @State() postalCode: string = null;
  @State() city: string = null;
  @State() streetAddress: string = null;
  @State() streetNumber: string = null;
  @State() validAddress: boolean = true;
  @State() translation: any;
  @State() postalCodeRequiredLabel: string = null;
  @State() streetRequiredLabel: string = null;

  @State() fullAddress: string = null;
  @State() isLoading: boolean = true;

  @Event() addresscomplete: EventEmitter<string>;

  fetchHelper = new FetchHelper('PostalCodeCityAutofill');

  componentWillLoad() {
    this.setInitialValues();
    this._postalCodeProps = this.setProps(this.postalCodeProps);
    this._cityProps = this.setProps(this.cityProps);
    this._streetAddressProps = this.setProps(this.streetAddressProps);
    this._streetNumberProps = this.setProps(this.streetNumberProps);
  }

  setProps(props) {
    if (typeof props === 'string') {
      return JSON.parse(props);
    } else {
      return props;
    }
  }

  componentDidLoad() {
    this.postalCodeInputField = this.pnInputPostalCode.querySelector('input');
    this.streetAddressInputField = this.pnInputStreetAddress.querySelector('input');
    this.streetNumberInputField = this.pnInputStreetNumber.querySelector('input');

    this.addEventHandlers();
  }

  async setInitialValues(href = window.location.href) {
    const marketWebContextService = new MarketWebContextService(href);

    if (!this.environment) {
      this.environment = await marketWebContextService.getEnvironmentName();
    }

    if (!this.endpoint && this.environment?.indexOf('local') === -1) {
      this.endpoint = await marketWebContextService.getEndpoint(this.environment, this.market);
    }

    if (!this.endpoint) {
      this.endpoint = '';
    }

    if (!this.language) {
      this.language = await marketWebContextService.getLanguage();
    }

    this.setTranslations()

    this.postalCodeRequiredLabel = "";
    this.streetRequiredLabel = "";

    if (this._postalCodeProps.postalCodeInputRequired === true) {
      this.postalCodeRequiredLabel = " (" + this.translation.required + ")";
    }

    if (this._streetAddressProps.streetAddressInputRequired === true) {
      this.streetRequiredLabel = " (" + this.translation.required + ")";
    }
  }

  setTranslations() {
    if (this.language === 'nb') {
      this.language = 'no';
    }
    if (this.language && translations[this.language]) {
      this.translation = translations[this.language];
    }
  }

  addEventHandlers() {
    let debounceTimeout: number | undefined;

    this.postalCodeInputField.addEventListener('input', (e) => {
      this.isLoading = true;
      clearTimeout(debounceTimeout);
      debounceTimeout = window.setTimeout(() => {
        this.fetchPostalCodeData(e);
      }, 300);
    });

    this.streetNumberInputField.addEventListener('input', debounce(this.fetchAddress.bind(this), 300));
    this.streetAddressInputField.addEventListener('keydown', () => { this.streetNumberInputField.value = '' });
    this.addFormFieldEvent();
  }

  addFormFieldEvent() {
    const allInputs = this.hostElement.querySelectorAll('input');
    allInputs.forEach(element => {
      ['keyup', 'change', 'blur'].forEach(eventName => {
        const debounceUpdate = debounce(this.updateHiddenValue.bind(this), 1000);
        element.addEventListener(eventName, debounceUpdate);
      });
    });
  }

  updateHiddenValue(e) {
    if (e.target === this.streetNumberInputField) {
      this.streetNumber = this.streetNumberInputField.value;
    }

    if (this.validAddress && this.postalCode && this.city && this.streetAddress && this.streetNumber) {
      this.fullAddress = `${this.streetAddress} ${this.streetNumber}, ${this.city} ${this.postalCode}`;
    } else {
      this.fullAddress = '';
    }

    this.addresscomplete.emit(this.fullAddress);
  }

  async fetchPostalCodeData(e: Event) {
    if (!e) {
      return;
    }
    const postalCode = this.postalCodeInputField.value;

    var postalCodeLength = 4;

    if (postalCode.length < postalCodeLength) {
      this.resetFields();
      return;
    }

    if (this.postalCode === postalCode) {
      return;
    }

    this.resetFields();
    this.postalCode = postalCode;

    var data = (await this.fetchPostalCode(postalCode));

    if (!data || !data.addresses) {
      this.city = null;
      this.isLoading = true;
      return;
    }

    const cityInfo = data.addresses?.find(addr => addr.postalCode === postalCode);
    this.city = cityInfo?.city || null;

    this.isLoading = false;
  }


  async fetchPostalCode(postalCode) {
    const endpointBase = this.endpoint.endsWith('/')
      ? this.endpoint.slice(0, -1)
      : this.endpoint;

    const fetchUrl = `${endpointBase}${this.endpointPath}?countryCode=${this.countryCode}&postalCode=${postalCode}`;

    try {
      const data = await this.fetchHelper.fetchJson(fetchUrl, { mode: 'cors' }, this.cache) as Root;

      if (!data?.addresses?.length) {
        this.pnInputPostalCode.setAttribute('error', this._postalCodeProps.postalCodeInputError);
        return;
      }
      return data;
    }
    catch (error) {
      console.error("Error fetching address data:", error);
      this.isLoading = true;
      return null;
    }
  }

  private resetFields() {
    this.streetAddressInputField.value = '';
    this.streetNumberInputField.value = '';
    this.city = null;
    this.streetAddress = null;
    this.streetNumber = null;
    this.validAddress = false;

    let inputs = document.getElementsByTagName('pn-input');

    Array.from(inputs).forEach(element => {
      element.removeAttribute('error');
    });
  }

  async fetchAddress() {
    const streetAddress = this.streetAddressInputField.value.trim();
    const streetNumber = this.streetNumberInputField.value.trim();

    if (!streetAddress || !streetNumber) {
      this.validAddress = false;
      return;
    }

    const endpointBase = this.endpoint.endsWith('/')
      ? this.endpoint.slice(0, -1)
      : this.endpoint;

    const fetchUrl = `${endpointBase}${this.endpointPath}?countryCode=${this.countryCode}&postalCode=${this.postalCodeInputField.value}&address=${streetAddress + " " + streetNumber}`;

    try {
      const data = await this.fetchHelper.fetchJson(fetchUrl, { mode: 'cors' }, this.cache) as Root;

      if (!data?.addresses?.length) {
        this.validAddress = false;
      } else {
        const inputStreetAddress = this.normalizeChar(streetAddress);
        const inputStreetNumber = this.normalizeChar(streetNumber);

        const addressFound = data.addresses.some(addr =>
          this.normalizeChar(addr.street) === inputStreetAddress &&
          this.normalizeChar(addr.houseNumber) === inputStreetNumber &&
          addr.postalCode === this.postalCodeInputField.value
        );

        this.validAddress = addressFound;
      }
    } catch (error) {
      console.error("Error fetching address data:", error);
      this.isLoading = true;
      this.validAddress = false;
    }

    if (this.streetAddress === streetAddress && this.streetNumber === streetNumber) {
      return;
    }

    if (streetAddress) {
      this.streetAddress = streetAddress;
    }
    if (streetNumber) {
      this.streetNumber = streetNumber;
    }
  }
  //this function is added to compare the street input values if entered without special characters like é, ø ect
  normalizeChar(str) {

    const specialCharMap = {
      'ø': 'o',
      'Ø': 'o',
      'æ': 'ae',
      'Æ': 'ae',
      // Add more as needed
    };

    var str1 = str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .split('').map(char => specialCharMap[char] || char)
      .join('').toLowerCase()
    return str1;
  }

  render() {
    return (
      <Host class="pn-address-autofill">
        {this._postalCodeProps?.postalCodeCityLabel ? (
          <div class="label-container">
            <label>{`${this._postalCodeProps.postalCodeCityLabel + ' ' + this.postalCodeRequiredLabel}`}</label>
          </div>
        ) : null}
        <div class="input-container-row">
          <pn-input
            type="number"
            name={this._postalCodeProps?.postalCodeInputName}
            required={this._postalCodeProps?.postalCodeInputRequired}
            placeholder={this._postalCodeProps?.postalCodeInputPlaceholder}
            ref={el => (this.pnInputPostalCode = el as HTMLElement)}
            autocomplete="off"
            helpertext={this._postalCodeProps?.postalCodeInputHelpertext}
          ></pn-input>

          <pn-input
            type="text"
            name={this._cityProps?.cityInputName}
            disabled
            value={this.city}
            placeholder={this._cityProps?.cityInputPlaceholder}
            autocomplete="off"
            helpertext={this._cityProps?.cityInputHelpertext}
          ></pn-input>
        </div>

        {this._streetAddressProps?.streetAddressNumberLabel ? (
          <div class="label-container">
            <label>{`${this._streetAddressProps.streetAddressNumberLabel + ' ' + this.streetRequiredLabel}`}</label>
          </div>
        ) : null}
        <div class="input-container-row">
          <pn-input
            type="text"
            name={this._streetAddressProps?.streetAddressInputName}
            required={this._streetAddressProps?.streetAddressInputRequired}
            value={this.streetAddress}
            placeholder={this._streetAddressProps?.streetAddressInputPlaceholder}
            ref={el => (this.pnInputStreetAddress = el as HTMLElement)}
            autocomplete="off"
            helpertext={this._streetAddressProps?.streetAddressInputHelpertext}
            disabled={this.isLoading}
          ></pn-input>

          <pn-input
            type="text"
            name={this._streetNumberProps?.streetNumberInputName}
            required={this._streetNumberProps?.streetNumberInputRequired}
            value={this.streetNumber}
            placeholder={this._streetNumberProps?.streetNumberInputPlaceholder}
            ref={el => (this.pnInputStreetNumber = el as HTMLElement)}
            autocomplete="off"
            helpertext={this._streetNumberProps?.streetNumberInputHelpertext}
            disabled={this.isLoading}
          ></pn-input>
        </div>
        {(!this.validAddress && this.streetAddressInputField.value && this.streetNumberInputField.value) ? (
          <div class="toast-container">
            <pn-toast text={this.addressNotFoundError ?? this.translation.addressNotFound} appearance="warning" icon={alert_exclamation_circle}></pn-toast>
          </div>
        ) : null}
      </Host>
    );
  }
}
